// ─── TMAI Membership framework — March 2022 ─────────────────────────────────
// Mirrors `cap-tmai-2022-03.ts`: the const IS the canonical runtime config
// (typed, versioned, no Firestore reads); the documentary JSON lives in
// `__docs/SEED-tmai-membership-2022-03.md`. The contract test
// (`__tests__/tmai-membership-2022-03.spec.ts`) pins the version and the
// structural invariants to detect drift.
//
// SOURCES & VERIFICATION:
// - Pathway-sourced criteria: `label.en` VERBATIM from
//   `__docs/sources/tmai/Pathway to Membership.pdf` (verified).
// - Statement of Intent §4 criteria: `label.en` VERBATIM from
//   `__docs/sources/tmai/statement-of-intent-2022-03-TRANSCRIPCION.md` (§3 + §4
//   transcribed from the rendered pages — the PDF is corrupt). The §4 matrix has
//   only Candidate/Member columns → NO Affiliate/Pre-Candidate rows; those stages
//   are thin by nature of the document, not by incomplete transcription.
// - `es` is a provisional translation pending theological review (like the CAP PLOs).
// - `sourceRef` is a string[] so a requirement demanded by BOTH documents cites
//   both (dedup over two sources — never two criteria for one requirement).
// - FAITHFUL DUPLICATION of the §4 matrix: a row with an X in BOTH the Candidate
//   and Member columns yields TWO criteria — one anchored to `candidate`, one to
//   `member` — because a Member's board must show every Member-column requirement,
//   regardless of it also being marked at Candidate. (This mirrors L4, which we
//   keep separate because it is re-demonstrated at each stage.) With duplication,
//   readiness for a target stage evaluates ONLY that stage's criteria — spec §3.4
//   holds as written; no cumulative carry-forward is needed.
export const TMAI_MEMBERSHIP_FRAMEWORK_2022_03 = {
    id: 'tmai-membership',
    version: '2022-03',
    stages: ['affiliate', 'preCandidate', 'candidate', 'member'],
    criteria: [
        // ── affiliate → preCandidate (Statement of Intent, six-step process) ──────
        {
            id: 'AFFIL_ADMIN_STATEMENT_OF_INTENT',
            stage: 'affiliate',
            category: 'administrative',
            label: {
                en: 'Submit the signed Statement of Intent formally notifying TMAI of the desire to pursue membership.',
                es: 'Presentar la Carta de Intención firmada notificando formalmente a TMAI el deseo de buscar la membresía.',
            },
            evidenceKind: 'upload',
            gating: true,
            sourceRef: ['Statement of Intent (mar-2022), §3 — Six Step Process, step 2'],
        },
        {
            id: 'AFFIL_ADMIN_SUMMIT_PARTICIPATION',
            stage: 'affiliate',
            category: 'administrative',
            label: {
                en: 'Partner with TMAI through summit attendance and regional networking prior to formal application.',
                es: 'Vincularse con TMAI mediante la asistencia a cumbres y la colaboración regional antes de la postulación formal.',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §3 — Six Step Process, step 1'],
        },
        // ── preCandidate → candidate (Pathway, Pre-Candidacy Criteria) ────────────
        {
            id: 'PRECAND_THEO_DOCTRINAL_AFFIRMATION',
            stage: 'preCandidate',
            category: 'theological',
            label: {
                en: 'Verbal affirmation of the TMAI doctrinal statement by all faculty and leadership',
                es: 'Afirmación verbal de la declaración doctrinal de TMAI por parte de toda la facultad y el liderazgo.',
            },
            evidenceKind: 'derived',
            derivedIndicator: 'DOCTRINAL_ATTESTATION_RATE',
            gating: true,
            sourceRef: ['Pathway to Membership, Pre-Candidacy Criteria — Theological'],
        },
        {
            id: 'PRECAND_ACAD_TRAINING_TRACK_RECORD',
            stage: 'preCandidate',
            category: 'academic',
            label: {
                en: 'Demonstrate a track record of training that is already taking place which will include (in time) the training of pastor-teachers for biblical ministry',
                es: 'Demostrar una trayectoria de formación ya en marcha, que con el tiempo incluirá la formación de pastores-maestros para el ministerio bíblico.',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Pathway to Membership, Pre-Candidacy Criteria — Academic'],
        },
        {
            id: 'PRECAND_ACAD_SENDING_CHURCH_NETWORK',
            stage: 'preCandidate',
            category: 'academic',
            label: {
                en: 'A network of local churches is in place which is sending students to be trained',
                es: 'Existe una red de iglesias locales que envía estudiantes a formarse.',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Pathway to Membership, Pre-Candidacy Criteria — Academic'],
        },
        {
            id: 'PRECAND_ADMIN_SUMMIT_PARTICIPATION',
            stage: 'preCandidate',
            category: 'administrative',
            label: {
                en: 'Participation at TMAI regional Summits',
                es: 'Participación en las cumbres regionales de TMAI.',
            },
            evidenceKind: 'upload',
            gating: false,
            sourceRef: ['Pathway to Membership, Pre-Candidacy Criteria — Administrative'],
        },
        {
            id: 'PRECAND_ADMIN_TRAINING_GOAL',
            stage: 'preCandidate',
            category: 'administrative',
            label: {
                en: 'Articulation of a clear goal for training (i.e., what will the end product look like)',
                es: 'Articulación de una meta clara de formación (es decir, cómo se ve el resultado final).',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Pathway to Membership, Pre-Candidacy Criteria — Administrative'],
        },
        {
            id: 'PRECAND_ADMIN_CHURCH_FINANCIAL_SUPPORT',
            stage: 'preCandidate',
            category: 'administrative',
            label: {
                en: 'A network of churches is in place which financially support the training ministry',
                es: 'Existe una red de iglesias que sostiene financieramente el ministerio de formación.',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Pathway to Membership, Pre-Candidacy Criteria — Administrative'],
        },
        {
            id: 'PRECAND_ADMIN_RECRUITMENT_PLAN',
            stage: 'preCandidate',
            category: 'administrative',
            label: {
                en: 'Demonstrate a plan for using existing as well as new networks to recruit students',
                es: 'Demostrar un plan para usar las redes existentes y nuevas en la captación de estudiantes.',
            },
            evidenceKind: 'upload',
            gating: false,
            sourceRef: ['Pathway to Membership, Pre-Candidacy Criteria — Administrative'],
        },
        {
            id: 'PRECAND_ADMIN_SENDING_AGENCY_APPROVAL',
            stage: 'preCandidate',
            category: 'administrative',
            label: {
                en: 'Approval by sending agency / local church to pursue TMAI membership',
                es: 'Aprobación de la agencia enviadora o iglesia local para buscar la membresía de TMAI.',
            },
            evidenceKind: 'upload',
            gating: true,
            sourceRef: ['Pathway to Membership, Pre-Candidacy Criteria — Administrative'],
        },
        {
            id: 'PRECAND_PERS_TMS_GRADUATES_ON_GROUND',
            stage: 'preCandidate',
            category: 'personnel',
            label: {
                en: '2 TMS graduates present on the ground',
                es: 'Dos egresados de TMS presentes en el lugar.',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Pathway to Membership, Pre-Candidacy Criteria — Personnel'],
        },
        {
            id: 'PRECAND_PERS_EXPAT_LANGUAGE_TRAINING',
            stage: 'preCandidate',
            category: 'personnel',
            label: {
                en: 'Completion of initial language and cultural training by expats',
                es: 'Finalización de la capacitación inicial de idioma y cultura por parte del personal expatriado.',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Pathway to Membership, Pre-Candidacy Criteria — Personnel'],
        },
        {
            id: 'PRECAND_PERS_LOCAL_CHURCH_INVOLVEMENT',
            stage: 'preCandidate',
            category: 'personnel',
            label: {
                en: 'Faculty and leaders are involved in local church ministry as language ability allows',
                es: 'La facultad y los líderes participan en el ministerio de la iglesia local en la medida en que su dominio del idioma lo permita.',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Pathway to Membership, Pre-Candidacy Criteria — Personnel'],
        },
        // ── candidate → member (Pathway, Candidacy Criteria + Appendix 1) ─────────
        {
            id: 'CAND_THEO_ANNUAL_DOCTRINAL_SIGNATURE',
            stage: 'candidate',
            // Category is `doctrinal` (Statement of Intent header) — this is the same
            // requirement as Pathway "Theological / sign annually" AND SIO §4 DOCTRINAL
            // D1; the signed document's taxonomy wins for the board header.
            category: 'doctrinal',
            label: {
                en: 'All faculty and board members sign the TMAI doctrinal statement annually',
                es: 'Toda la facultad y los miembros de la junta firman anualmente la declaración doctrinal de TMAI.',
            },
            evidenceKind: 'derived',
            derivedIndicator: 'DOCTRINAL_ATTESTATION_RATE',
            gating: true,
            sourceRef: [
                'Pathway to Membership, Candidacy Criteria — Theological',
                'Statement of Intent (mar-2022), §4 DOCTRINAL — D1',
            ],
        },
        {
            id: 'CAND_ACAD_COURSE_DESCRIPTIONS',
            stage: 'candidate',
            category: 'academic',
            label: {
                en: 'Course descriptions must be done for each course being taught for each program (a template will be provided)',
                es: 'Descripciones de curso completadas para cada curso impartido en cada programa (se proveerá una plantilla).',
            },
            evidenceKind: 'derived',
            derivedIndicator: 'CCD_COVERAGE',
            gating: true,
            sourceRef: ['Pathway to Membership, Candidacy Criteria — Academic'],
        },
        {
            id: 'CAND_ACAD_CONSISTENT_MINISTRY',
            stage: 'candidate',
            category: 'academic',
            label: {
                en: 'TC must demonstrate a consistent training ministry for the entirety of the 2-year candidacy process',
                es: 'El centro debe demostrar un ministerio de formación consistente durante todo el proceso de candidatura de dos años.',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Pathway to Membership, Candidacy Criteria — Academic'],
        },
        {
            id: 'CAND_ACAD_STUDENT_PIPELINE',
            stage: 'candidate',
            category: 'academic',
            label: {
                en: 'TC must demonstrate that it has developed a pipeline of students to train, both currently and into the future',
                es: 'El centro debe demostrar que ha desarrollado un flujo de estudiantes a formar, tanto actualmente como a futuro.',
            },
            evidenceKind: 'derived',
            derivedIndicator: 'ENROLLED_STUDENT_COUNT',
            gating: false,
            sourceRef: ['Pathway to Membership, Candidacy Criteria — Academic'],
        },
        {
            id: 'CAND_ACAD_COMPLETE_DEGREE_PROGRAMS',
            stage: 'candidate',
            category: 'academic',
            label: {
                en: 'TC must have complete degree programs for the level that they are targeting. That is, if a Preaching Certificate is being given, the TC must have the full curriculum for this program documented',
                es: 'El centro debe tener programas completos para el nivel al que apunta. Es decir, si se otorga un Certificado en Predicación, debe tener documentada la malla completa de ese programa.',
            },
            evidenceKind: 'derived',
            derivedIndicator: 'PROGRAM_CURRICULUM_DOCUMENTED',
            gating: true,
            sourceRef: ['Pathway to Membership, Candidacy Criteria — Academic'],
        },
        {
            id: 'CAND_ACAD_BSC_CAP',
            stage: 'candidate',
            category: 'academicProgram',
            label: {
                en: 'Offer at least the TMAI Biblical Studies Certificate as a recognized Certified Academic Program.',
                es: 'Ofrecer al menos el Certificado en Estudios Bíblicos de TMAI como Programa Académico Certificado reconocido.',
            },
            evidenceKind: 'derived',
            derivedIndicator: 'CAP_BSC_STATUS',
            gating: true,
            sourceRef: [
                'Statement of Intent (mar-2022), §4 ACADEMIC PROGRAM — P2',
                'Statement of Intent (mar-2022), §3 — Candidate category definition',
            ],
        },
        {
            id: 'CAND_ADMIN_SUMMIT_PARTICIPATION',
            stage: 'candidate',
            category: 'administrative',
            label: {
                en: 'Participation at TMAI Regional Summits',
                es: 'Participación en las cumbres regionales de TMAI.',
            },
            evidenceKind: 'upload',
            gating: false,
            sourceRef: ['Pathway to Membership, Candidacy Criteria — Administrative'],
        },
        {
            id: 'CAND_ADMIN_PARTNER_TC_SITE_VISITS',
            stage: 'candidate',
            category: 'administrative',
            label: {
                en: 'Candidate TC must partner with a current TMAI TC. The current TC will be designated by the TMAI board. This partnership includes: 2 site visits to the designated training center by the candidate school; 1 visit by TMAI member school to candidate school during the candidacy period (with an evaluation form filled out)',
                es: 'El centro candidato debe asociarse con un centro TMAI vigente, designado por la junta de TMAI. La asociación incluye: 2 visitas del centro candidato al centro designado; 1 visita del centro miembro al candidato durante el período de candidatura (con formulario de evaluación completado).',
            },
            evidenceKind: 'upload',
            gating: false,
            sourceRef: ['Pathway to Membership, Candidacy Criteria — Administrative'],
        },
        {
            id: 'CAND_ADMIN_ADVISORY_BOARD_COMPOSITION',
            stage: 'candidate',
            category: 'administrative',
            label: {
                en: 'If the TC is considering the formation of an advisory board, TMAI requires that the board include at least one national leader and that membership on the board last for one year only, with renewal each year.',
                es: 'Si el centro considera formar un consejo asesor, TMAI exige que el consejo incluya al menos un líder nacional y que la membresía en el consejo dure solo un año, con renovación anual.',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Pathway to Membership, Candidacy Criteria — Administrative'],
        },
        {
            id: 'CAND_PERS_ELDER_QUALIFICATION_AFFIRMATION',
            stage: 'candidate',
            category: 'personnel',
            label: {
                en: 'There is affirmation from the sending agency/local churches, and TC leadership that the faculty members and leadership of the training center meet the biblical qualifications for an elder (1 Tim 3, Titus 1) and are ordained or on the path to ordination as pastor-teachers.',
                es: 'Hay afirmación de la agencia enviadora/iglesias locales y del liderazgo del centro de que la facultad y el liderazgo cumplen las cualificaciones bíblicas de anciano (1 Ti 3, Tito 1) y están ordenados o en camino a la ordenación como pastores-maestros.',
            },
            evidenceKind: 'upload',
            gating: true,
            sourceRef: ['Pathway to Membership, Candidacy Criteria — Personnel'],
        },
        {
            id: 'CAND_PERS_ELDER_BOARD_ACCOUNTABILITY',
            stage: 'candidate',
            category: 'personnel',
            label: {
                en: 'The faculty and leaders of the center are all involved in local-church ministry and accountable to an elder board in personal, family, and ministerial life.',
                es: 'La facultad y los líderes del centro participan en el ministerio de la iglesia local y rinden cuentas a un consejo de ancianos en su vida personal, familiar y ministerial.',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Pathway to Membership, Candidacy Criteria — Personnel'],
        },
        {
            id: 'CAND_PERS_ACCOUNTABILITY_PLAN',
            stage: 'candidate',
            category: 'personnel',
            label: {
                en: 'The training center can demonstrate that it has in place a good plan for accountability which ensures that the members of the team (both expats and nationals) will be nurtured spiritually and admonished when needed.',
                es: 'El centro puede demostrar que tiene un buen plan de rendición de cuentas que asegura que los miembros del equipo (expatriados y nacionales) sean nutridos espiritualmente y amonestados cuando sea necesario.',
            },
            evidenceKind: 'upload',
            gating: false,
            sourceRef: ['Pathway to Membership, Candidacy Criteria — Personnel'],
        },
        {
            id: 'CAND_ACAD_PROGRAM_MISSION_STATEMENTS',
            stage: 'candidate',
            category: 'academic',
            label: {
                en: 'Mission statement for each degree program offered',
                es: 'Declaración de propósito de cada programa ofrecido.',
            },
            evidenceKind: 'derived',
            derivedIndicator: 'PROGRAM_MISSION_DEFINED',
            gating: true,
            sourceRef: ['Pathway to Membership, Appendix 1 — Academic'],
        },
        {
            id: 'CAND_ACAD_PROGRAM_DESCRIPTIONS',
            stage: 'candidate',
            category: 'academic',
            label: {
                en: 'Description of each degree program',
                es: 'Descripción de cada programa ofrecido.',
            },
            evidenceKind: 'derived',
            derivedIndicator: 'PROGRAM_DESCRIPTION_DOCUMENTED',
            gating: true,
            sourceRef: ['Pathway to Membership, Appendix 1 — Academic'],
        },
        {
            id: 'CAND_ACAD_COURSE_OFFERINGS_CALENDAR',
            stage: 'candidate',
            category: 'academic',
            label: {
                en: 'Course offerings calendars for the next two years',
                es: 'Calendarios de oferta de cursos para los próximos dos años.',
            },
            evidenceKind: 'upload',
            gating: false,
            sourceRef: [
                'Pathway to Membership, Appendix 1 — Academic',
                'Statement of Intent (mar-2022), §4 ACADEMIC PROGRAM — P5',
            ],
        },
        {
            id: 'CAND_ADMIN_BUDGET_BY_PROGRAM',
            stage: 'candidate',
            category: 'administrative',
            label: {
                en: 'Full Training Center budget, broken out by program',
                es: 'Presupuesto completo del centro de formación, desglosado por programa.',
            },
            evidenceKind: 'upload',
            gating: true,
            sourceRef: ['Pathway to Membership, Appendix 1 — Administrative'],
        },
        {
            id: 'CAND_PERS_FACULTY_ROSTER',
            stage: 'candidate',
            category: 'personnel',
            label: {
                en: 'Listing of all faculty and advisory council members',
                es: 'Nómina de toda la facultad y de los miembros del consejo asesor.',
            },
            evidenceKind: 'derived',
            derivedIndicator: 'FACULTY_ROSTER_SIZE',
            gating: true,
            sourceRef: ['Pathway to Membership, Appendix 1 — Personnel'],
        },
        {
            id: 'CAND_PERS_STUDENT_GRADUATE_ROSTER',
            stage: 'candidate',
            category: 'personnel',
            label: {
                en: 'Listing of students and graduates including their locations and ministries',
                es: 'Nómina de estudiantes y egresados incluyendo su ubicación y ministerio.',
            },
            evidenceKind: 'derived',
            derivedIndicator: 'GRADUATE_COUNT',
            gating: false,
            sourceRef: ['Pathway to Membership, Appendix 1 — Personnel'],
        },
        {
            id: 'CAND_ADMIN_ONE_AND_FIVE_YEAR_PLAN',
            stage: 'candidate',
            category: 'administrative',
            // Pathway asks a 1-and-5-year plan; the Statement of Intent §4 (L5/L6) asks
            // 3 (Candidate) and 5 (Member) — a real numeric conflict, not wording. The
            // signed SIO prevails (see DECISIONS); this Pathway criterion is retained but
            // pending clarification with TMAI, and the SIO L5/L6 criteria carry the 3/5.
            label: {
                en: 'One-year and five-year plan following the TMAI template, covering academic program goals, financial projections, financial self-sustainment strategy, literature and resource needs, and long-term indigenization strategy.',
                es: 'Plan a uno y cinco años según la plantilla de TMAI, cubriendo metas de programas académicos, proyecciones financieras, estrategia de autosustentabilidad, necesidades de literatura y recursos, y estrategia de indigenización a largo plazo.',
            },
            evidenceKind: 'upload',
            gating: true,
            sourceRef: [
                'Pathway to Membership, Appendix 1 — 1-year and 5-year plan',
                'NOTE: conflicts with Statement of Intent §4 L5/L6 (3-year Candidate / 5-year Member) — pending clarification with TMAI',
            ],
        },
        // ── member (sustaining — Statement of Intent) ─────────────────────────────
        {
            id: 'MEMBER_ACAD_DEP_CAP',
            stage: 'member',
            category: 'academicProgram',
            label: {
                en: 'Offer at least the TMAI Diploma in Expository Preaching as a recognized Certified Academic Program.',
                es: 'Ofrecer al menos el Diploma en Predicación Expositiva de TMAI como Programa Académico Certificado reconocido.',
            },
            evidenceKind: 'derived',
            derivedIndicator: 'CAP_DEP_STATUS',
            gating: true,
            sourceRef: [
                'Statement of Intent (mar-2022), §4 ACADEMIC PROGRAM — P3',
                'Statement of Intent (mar-2022), §3 — Member category definition',
            ],
        },
        {
            id: 'MEMBER_ADMIN_ANNUAL_STANDARDS_REVIEW',
            stage: 'member',
            category: 'administrative',
            label: {
                en: 'Maintain TMAI academic, administrative and financial standards, reviewed annually by the membership committee.',
                es: 'Mantener los estándares académicos, administrativos y financieros de TMAI, revisados anualmente por el comité de membresía.',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §3 — Six Step Process, step 6'],
        },
        // ══ Statement of Intent §4 — Membership Requirements matrix ══════════════
        // Two columns only (Candidate / Member). Both-column rows anchored to
        // `candidate` (cumulative ladder — see header). `en` verbatim from the
        // transcription; `es` provisional.
        // ── LEADERSHIP ────────────────────────────────────────────────────────────
        {
            id: 'CAND_LEAD_GOVERNANCE_STRUCTURE',
            stage: 'candidate',
            category: 'leadership',
            label: {
                en: 'Adopt a governance and staffing structure appropriate to theological institutions according to the standards and criteria in the TMAI Handbook.',
                es: 'Adoptar una estructura de gobierno y de personal apropiada para instituciones teológicas según los estándares y criterios del TMAI Handbook.',
            },
            evidenceKind: 'upload',
            gating: true,
            sourceRef: ['Statement of Intent (mar-2022), §4 LEADERSHIP — L1'],
        },
        {
            id: 'CAND_LEAD_QUALIFIED_LEADERSHIP',
            stage: 'candidate',
            category: 'leadership',
            label: {
                en: 'Demonstrate and uphold the availability and qualifications of those leading the Training Ministry. At least 1 TMS graduate or man with a comparable TMAI connection and academic degree must be part of the training center leadership and faculty, approved by the TMAI Membership committee. The leadership of the Training Ministry must be elder-qualified (ordained or on the path to ordination) and consist of a plurality of leaders from multiple churches (or a plurality of elders at a local church).',
                es: 'Demostrar y sostener la disponibilidad y las cualificaciones de quienes lideran el ministerio de formación. Al menos 1 egresado de TMS (o un hombre con una conexión TMAI comparable y grado académico) debe ser parte del liderazgo y la facultad del centro, aprobado por el comité de Membresía de TMAI. El liderazgo debe estar cualificado como anciano (ordenado o en camino a la ordenación) y consistir en una pluralidad de líderes de varias iglesias (o una pluralidad de ancianos en una iglesia local).',
            },
            evidenceKind: 'upload',
            gating: true,
            sourceRef: ['Statement of Intent (mar-2022), §4 LEADERSHIP — L2'],
        },
        {
            id: 'MEMBER_LEAD_QUALIFIED_LEADERSHIP',
            stage: 'member',
            category: 'leadership',
            label: {
                en: 'Demonstrate and uphold the availability and qualifications of those leading the Training Ministry. At least 2 TMS graduates or men with comparable TMAI connections and academic degrees must be part of the training center leadership and faculty, approved by the TMAI Membership committee. The leadership of the Training Ministry must be elder-qualified (ordained or on the path to ordination) and consist of a plurality of leaders from multiple churches (or a plurality of elders at a local church).',
                es: 'Demostrar y sostener la disponibilidad y las cualificaciones de quienes lideran el ministerio de formación. Al menos 2 egresados de TMS (o hombres con conexiones TMAI comparables y grados académicos) deben ser parte del liderazgo y la facultad del centro, aprobados por el comité de Membresía de TMAI. El liderazgo debe estar cualificado como anciano (ordenado o en camino a la ordenación) y consistir en una pluralidad de líderes de varias iglesias (o una pluralidad de ancianos en una iglesia local).',
            },
            evidenceKind: 'upload',
            gating: true,
            sourceRef: ['Statement of Intent (mar-2022), §4 LEADERSHIP — L3'],
        },
        {
            id: 'CAND_LEAD_SENDING_AGENCY_APPROVAL',
            stage: 'candidate',
            category: 'leadership',
            // RECURRING requirement: sending-agency approval is asked in pre-candidacy
            // (PRECAND_ADMIN_SENDING_AGENCY_APPROVAL) and asked AGAIN, per leader, at
            // Candidate/Member. NOT merged — meeting it once must not mark as met
            // something that has to be demonstrated again two stages later.
            label: {
                en: 'Each leader of the Training Ministry has to show approval by their sending agency and/or local church to pursue TMAI Partnership.',
                es: 'Cada líder del ministerio de formación debe mostrar la aprobación de su agencia enviadora y/o iglesia local para buscar la asociación con TMAI.',
            },
            evidenceKind: 'upload',
            gating: true,
            sourceRef: ['Statement of Intent (mar-2022), §4 LEADERSHIP — L4'],
        },
        {
            id: 'CAND_LEAD_THREE_YEAR_PLAN',
            stage: 'candidate',
            category: 'leadership',
            label: {
                en: 'A 3-year ministry plan must be in place, presented to the TMAI Membership office and regional leaders and approved by the TMAI leadership.',
                es: 'Debe existir un plan ministerial a 3 años, presentado a la oficina de Membresía de TMAI y a los líderes regionales, y aprobado por el liderazgo de TMAI.',
            },
            evidenceKind: 'upload',
            gating: true,
            sourceRef: ['Statement of Intent (mar-2022), §4 LEADERSHIP — L5'],
        },
        {
            id: 'MEMBER_LEAD_FIVE_YEAR_PLAN',
            stage: 'member',
            category: 'leadership',
            label: {
                en: 'A 5-year ministry plan must be in place, presented to the TMAI Membership office and regional leaders and approved by the TMAI leadership.',
                es: 'Debe existir un plan ministerial a 5 años, presentado a la oficina de Membresía de TMAI y a los líderes regionales, y aprobado por el liderazgo de TMAI.',
            },
            evidenceKind: 'upload',
            gating: true,
            sourceRef: ['Statement of Intent (mar-2022), §4 LEADERSHIP — L6'],
        },
        // ── FINANCES ──────────────────────────────────────────────────────────────
        {
            id: 'CAND_FIN_BUDGET_PLAN_ALIGN',
            stage: 'candidate',
            category: 'finances',
            label: {
                en: 'Submit a budget plan, evaluated by TMAI leadership, and begin to align with the financial administration standards set forth in the TMAI Handbook.',
                es: 'Presentar un plan de presupuesto, evaluado por el liderazgo de TMAI, y comenzar a alinearse con los estándares de administración financiera del TMAI Handbook.',
            },
            evidenceKind: 'upload',
            gating: true,
            sourceRef: ['Statement of Intent (mar-2022), §4 FINANCES — F1'],
        },
        {
            id: 'MEMBER_FIN_BUDGET_PLAN_FULL_ALIGN',
            stage: 'member',
            category: 'finances',
            label: {
                en: 'Submit a budget plan, evaluated and approved by the TMAI leadership, and align fully with the financial administration standards set forth in the TMAI Handbook.',
                es: 'Presentar un plan de presupuesto, evaluado y aprobado por el liderazgo de TMAI, y alinearse plenamente con los estándares de administración financiera del TMAI Handbook.',
            },
            evidenceKind: 'upload',
            gating: true,
            sourceRef: ['Statement of Intent (mar-2022), §4 FINANCES — F2'],
        },
        {
            id: 'CAND_FIN_CHARITABLE_PURSUING',
            stage: 'candidate',
            category: 'finances',
            label: {
                en: 'Be actively pursuing the establishment of a charitable organization in the country of operation (unless reasonable security concerns prevent this).',
                es: 'Estar gestionando activamente la constitución de una organización sin fines de lucro en el país de operación (salvo que preocupaciones de seguridad razonables lo impidan).',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 FINANCES — F3'],
        },
        {
            id: 'MEMBER_FIN_CHARITABLE_ESTABLISHED',
            stage: 'member',
            category: 'finances',
            label: {
                en: 'Be an established charitable organization in the country of operation (unless reasonable security concerns prevent this).',
                es: 'Ser una organización sin fines de lucro constituida en el país de operación (salvo que preocupaciones de seguridad razonables lo impidan).',
            },
            evidenceKind: 'upload',
            gating: true,
            sourceRef: ['Statement of Intent (mar-2022), §4 FINANCES — F4'],
        },
        {
            id: 'CAND_FIN_REGULATIONS_PLAN',
            stage: 'candidate',
            category: 'finances',
            label: {
                en: 'Have a plan in place for meeting all local regulations and standards (unless reasonable security concerns prevent this).',
                es: 'Tener un plan para cumplir todas las regulaciones y estándares locales (salvo que preocupaciones de seguridad razonables lo impidan).',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 FINANCES — F5'],
        },
        {
            id: 'MEMBER_FIN_ACCOUNTING_REGULATIONS',
            stage: 'member',
            category: 'finances',
            label: {
                en: 'Meet all local accounting regulations and standards (unless reasonable security concerns prevent this).',
                es: 'Cumplir todas las regulaciones y estándares contables locales (salvo que preocupaciones de seguridad razonables lo impidan).',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 FINANCES — F6'],
        },
        {
            id: 'CAND_FIN_CONTROLS_PLAN',
            stage: 'candidate',
            category: 'finances',
            label: {
                en: 'Have a plan in place for establishing proper financial controls and segregation of financial duties among staff in order to prevent fraud, embezzlement, or misuse.',
                es: 'Tener un plan para establecer controles financieros adecuados y la segregación de funciones financieras entre el personal, a fin de prevenir fraude, malversación o uso indebido.',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 FINANCES — F7'],
        },
        {
            id: 'MEMBER_FIN_CONTROLS_DEMONSTRATED',
            stage: 'member',
            category: 'finances',
            label: {
                en: 'Demonstrate proper financial controls and segregation of financial duties among staff in order to prevent fraud, embezzlement, or misuse.',
                es: 'Demostrar controles financieros adecuados y la segregación de funciones financieras entre el personal, a fin de prevenir fraude, malversación o uso indebido.',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 FINANCES — F8'],
        },
        {
            id: 'CAND_FIN_ANNUAL_BUDGET',
            stage: 'candidate',
            category: 'finances',
            label: {
                en: 'Submit an annual revenue and expense budget.',
                es: 'Presentar un presupuesto anual de ingresos y egresos.',
            },
            evidenceKind: 'upload',
            gating: true,
            sourceRef: ['Statement of Intent (mar-2022), §4 FINANCES — F9'],
        },
        {
            id: 'CAND_FIN_ANNUAL_ACTUALS_REPORT',
            stage: 'candidate',
            category: 'finances',
            label: {
                en: 'Provide annual reporting on actual revenue and expenses vs. budget, as well as reporting on how operating grant funds were used.',
                es: 'Proveer un informe anual de ingresos y egresos reales vs. presupuesto, además del informe de cómo se usaron los fondos de subvención operativa.',
            },
            evidenceKind: 'upload',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 FINANCES — F10'],
        },
        {
            id: 'CAND_FIN_QUARTERLY_GRANT_REPORT',
            stage: 'candidate',
            category: 'finances',
            label: {
                en: 'Provide quarterly reporting on how special project grant funds were used (i.e. construction projects, technology upgrades, translation projects, etc.).',
                es: 'Proveer informes trimestrales de cómo se usaron los fondos de subvención de proyectos especiales (p. ej. construcción, mejoras tecnológicas, proyectos de traducción, etc.).',
            },
            evidenceKind: 'upload',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 FINANCES — F11'],
        },
        {
            id: 'CAND_FIN_ACCURATE_RECORDS',
            stage: 'candidate',
            category: 'finances',
            label: {
                en: 'Keep accurate financial records, receipts, and substantiation of expenses.',
                es: 'Mantener registros financieros, recibos y sustentación de gastos precisos.',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 FINANCES — F12'],
        },
        {
            id: 'CAND_FIN_REPUTABLE_SOFTWARE',
            stage: 'candidate',
            category: 'finances',
            label: {
                en: 'Utilize a recognized and reputable financial software.',
                es: 'Utilizar un software financiero reconocido y de buena reputación.',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 FINANCES — F13'],
        },
        // ── ADMINISTRATIVE ────────────────────────────────────────────────────────
        {
            id: 'CAND_ADMIN_ORG_CHART',
            stage: 'candidate',
            category: 'administrative',
            // Criterion NEW here; the packet item PKT_PERS_ORG_CHART stays as the
            // deliverable pointer (unchanged).
            label: {
                en: 'Demonstrate an administrative structure for theological institutions according to the standards and criteria of TMAI in the TMAI Handbook. An org-chart must be in place that demonstrates the core functions of a TMAI Training Ministry (listing all staff, faculty, and board members).',
                es: 'Demostrar una estructura administrativa para instituciones teológicas según los estándares y criterios de TMAI en el TMAI Handbook. Debe existir un organigrama que demuestre las funciones centrales de un ministerio de formación TMAI (con todo el personal, facultad y miembros de la junta).',
            },
            evidenceKind: 'upload',
            gating: true,
            sourceRef: ['Statement of Intent (mar-2022), §4 ADMINISTRATIVE — A1'],
        },
        {
            id: 'CAND_ADMIN_STAFF_DISCIPLESHIP_PLAN',
            stage: 'candidate',
            category: 'administrative',
            label: {
                en: 'A plan must be in place to disciple and train staff and faculty.',
                es: 'Debe existir un plan para discipular y capacitar al personal y a la facultad.',
            },
            evidenceKind: 'upload',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 ADMINISTRATIVE — A2'],
        },
        {
            id: 'MEMBER_ADMIN_STUDENT_RECORDS_SYSTEM',
            stage: 'member',
            category: 'administrative',
            // A3 models the CAPABILITY (retain student records for class/program/faculty
            // evaluations + transfer of units), NOT the products. The row names
            // Canvas/Moodle/Classter as mandatory products for Member schools — that is
            // an OPEN point with TMAI, kept in sourceRef for traceability, not modeled.
            label: {
                en: 'Demonstrate the ability to retain student records for class, program, and faculty evaluations and the transfer of units.',
                es: 'Demostrar la capacidad de retener expedientes de estudiantes para evaluaciones de clase, programa y facultad, y la transferencia de unidades.',
            },
            evidenceKind: 'upload',
            gating: true,
            sourceRef: [
                'Statement of Intent (mar-2022), §4 ADMINISTRATIVE — A3',
                'A3 names Canvas/Moodle/Classter as mandatory products for Member schools — open point with TMAI; modeled as capability, not product',
            ],
        },
        {
            id: 'CAND_ADMIN_RECRUITMENT_STRUCTURE',
            stage: 'candidate',
            category: 'administrative',
            label: {
                en: 'Present a structure for using existing as well as new networks and media to recruit new students. This includes a network of local churches in-country who are committed to sending students to the training center.',
                es: 'Presentar una estructura para usar redes y medios existentes y nuevos en la captación de nuevos estudiantes. Incluye una red de iglesias locales en el país comprometidas a enviar estudiantes al centro.',
            },
            evidenceKind: 'upload',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 ADMINISTRATIVE — A4'],
        },
        {
            id: 'MEMBER_ADMIN_RECRUITMENT_RECORDS',
            stage: 'member',
            category: 'administrative',
            label: {
                en: 'Present a plan, structure and records of using existing as well as new networks and media for the recruitment of new students.',
                es: 'Presentar un plan, estructura y registros del uso de redes y medios existentes y nuevos para la captación de nuevos estudiantes.',
            },
            evidenceKind: 'upload',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 ADMINISTRATIVE — A5'],
        },
        {
            id: 'CAND_ADMIN_SUPPORT_NETWORK',
            stage: 'candidate',
            category: 'administrative',
            label: {
                en: 'A network of national and international churches (or ministry partners) is in place who financially and prayerfully support the theological Training Ministry. Sufficient efforts in donor relations must take place.',
                es: 'Existe una red de iglesias nacionales e internacionales (o socios ministeriales) que sostiene financiera y espiritualmente al ministerio de formación. Deben realizarse esfuerzos suficientes en relaciones con donantes.',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 ADMINISTRATIVE — A6'],
        },
        {
            id: 'MEMBER_ADMIN_SUPPORT_NETWORK_ESTABLISHED',
            stage: 'member',
            category: 'administrative',
            label: {
                en: 'A network of national and international churches is established who financially and prayerfully support the Training Ministry. Donor relations are established, with an approved plan of annual donor initiatives.',
                es: 'Existe una red establecida de iglesias nacionales e internacionales que sostiene financiera y espiritualmente al ministerio de formación. Las relaciones con donantes están establecidas, con un plan aprobado de iniciativas anuales.',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 ADMINISTRATIVE — A7'],
        },
        {
            id: 'CAND_ADMIN_STUDENT_ALUMNI_DISCIPLESHIP',
            stage: 'candidate',
            category: 'administrative',
            label: {
                en: 'Present an intentional structure of discipling current students, as well as guiding alumni in collaboration with the local church.',
                es: 'Presentar una estructura intencional de discipulado de los estudiantes actuales, así como de acompañamiento de egresados en colaboración con la iglesia local.',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 ADMINISTRATIVE — A8'],
        },
        {
            id: 'MEMBER_ADMIN_STUDENT_ALUMNI_SYSTEMATIC',
            stage: 'member',
            category: 'administrative',
            label: {
                en: 'A systematic and intentional plan must be demonstrated to disciple and mentor current students as well as alumni.',
                es: 'Debe demostrarse un plan sistemático e intencional para discipular y mentorear a los estudiantes actuales y a los egresados.',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 ADMINISTRATIVE — A9'],
        },
        {
            id: 'MEMBER_ADMIN_SECURE_FINANCIAL_SOFTWARE',
            stage: 'member',
            category: 'administrative',
            label: {
                en: 'The use of a secure financial software is mandatory for Members for all financial recording, documentation, and communication with the TMAI office (grants, etc.).',
                es: 'El uso de un software financiero seguro es obligatorio para los Miembros en todo registro y documentación financiera y en la comunicación con la oficina de TMAI (subvenciones, etc.).',
            },
            evidenceKind: 'declaration',
            gating: true,
            sourceRef: ['Statement of Intent (mar-2022), §4 ADMINISTRATIVE — A10'],
        },
        {
            id: 'CAND_ADMIN_TECHNOLOGY_SUGGESTED',
            stage: 'candidate',
            category: 'administrative',
            label: {
                en: 'The use of technology to support and advance the growing ministries is suggested, for example in the areas of online classes, conferences, seminars, etc.',
                es: 'Se sugiere el uso de tecnología para apoyar y hacer avanzar los ministerios en crecimiento, por ejemplo en clases en línea, conferencias, seminarios, etc.',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 ADMINISTRATIVE — A11'],
        },
        {
            id: 'MEMBER_ADMIN_TECHNOLOGY_DEMONSTRATED',
            stage: 'member',
            category: 'administrative',
            label: {
                en: 'Demonstrate the use of technology to support and advance the growing ministries, for example in the areas of online classes, conferences, seminars, etc.',
                es: 'Demostrar el uso de tecnología para apoyar y hacer avanzar los ministerios en crecimiento, por ejemplo en clases en línea, conferencias, seminarios, etc.',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 ADMINISTRATIVE — A12'],
        },
        // ── ACADEMIC PROGRAM ──────────────────────────────────────────────────────
        {
            id: 'CAND_ACADPROG_STARTUP_BLOCKS',
            stage: 'candidate',
            category: 'academicProgram',
            label: {
                en: 'Candidate Training Ministries who are still in the formative, beginning stage have to offer the start-up blocks of TMAI (conferences, seminars, radio, media, publication, church-based training, etc.).',
                es: 'Los ministerios de formación candidatos que aún están en la etapa formativa e inicial deben ofrecer los "start-up blocks" de TMAI (conferencias, seminarios, radio, medios, publicaciones, formación basada en la iglesia, etc.).',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 ACADEMIC PROGRAM — P1'],
        },
        {
            id: 'MEMBER_ACADPROG_BSC_COHORT_GRADUATED',
            stage: 'member',
            category: 'academicProgram',
            // P3 bundles two independent conditions: offering the DEP CAP (→
            // MEMBER_ACAD_DEP_CAP) and having graduated a full BSC cohort. This is the
            // cohort condition — a TC can offer the DEP without having graduated a cohort,
            // which is exactly what TMAI wants to prevent.
            label: {
                en: 'One complete cohort of BSC students need to have graduated.',
                es: 'Una cohorte completa de estudiantes del BSC debe haberse graduado.',
            },
            evidenceKind: 'derived',
            derivedIndicator: 'BSC_COHORT_GRADUATED',
            gating: true,
            sourceRef: ['Statement of Intent (mar-2022), §4 ACADEMIC PROGRAM — P3'],
        },
        {
            id: 'CAND_ACADPROG_ANNUAL_CAP_REPORT',
            stage: 'candidate',
            category: 'academicProgram',
            label: {
                en: 'Completion and submission of the "Annual CAP Report."',
                es: 'Completar y presentar el "Annual CAP Report".',
            },
            evidenceKind: 'upload',
            gating: true,
            sourceRef: ['Statement of Intent (mar-2022), §4 ACADEMIC PROGRAM — P4'],
        },
        // ── VISION & MISSION FOR MATURITY ─────────────────────────────────────────
        {
            id: 'CAND_VISION_MATURITY_PRIORITIES',
            stage: 'candidate',
            category: 'visionMissionMaturity',
            label: {
                en: 'The vision and mission of TMAI for its members and partners is to mature and multiply, so that TMAI members and partners are able through their faithful ministry to grow in all areas of their ministries for a long-lasting ministry that brings glory to God. There are three areas in the maturity process that TMAI gives priority to: Indigenization (the discipleship and training of Nationals is one of the core Ministry Distinctives of TMAI); Self-Sustainability (it needs to be the goal and mission of a TMAI partner to become more and more self-sustainable); Academic Growth (the default approach of TMAI for new partners is to offer basic theological training at the undergraduate level as the beginning phase of each theological training, but with the clear strategic plan to grow and mature over time to more advanced training — not for all). All three areas are part of the national- and regional-strategic plan of TMAI.',
                es: 'La visión y misión de TMAI para sus miembros y socios es madurar y multiplicarse, de modo que crezcan en todas las áreas de su ministerio para un ministerio duradero que glorifique a Dios. TMAI prioriza tres áreas del proceso de madurez: Indigenización (el discipulado y la formación de nacionales es uno de los distintivos ministeriales centrales de TMAI); Autosustentabilidad (debe ser la meta y misión de un socio de TMAI volverse cada vez más autosustentable); Crecimiento Académico (el enfoque por defecto de TMAI para nuevos socios es ofrecer formación teológica básica de nivel de pregrado como fase inicial, pero con un plan estratégico claro de crecer y madurar con el tiempo hacia formación más avanzada — no para todos). Las tres áreas son parte del plan estratégico nacional y regional de TMAI.',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 VISION & MISSION FOR MATURITY — V1'],
        },
        // ══ Member-stage twins of the §4 rows marked X in BOTH columns ═══════════
        // Faithful duplication: L1, L4, F9-F13, D1, A1, A2, P2, P4, P5, V1 each carry
        // the same requirement at Member as at Candidate — so each gets a member
        // criterion too (its Candidate instance is above). `en` verbatim from §4.
        {
            id: 'MEMBER_LEAD_GOVERNANCE_STRUCTURE',
            stage: 'member',
            category: 'leadership',
            label: {
                en: 'Adopt a governance and staffing structure appropriate to theological institutions according to the standards and criteria in the TMAI Handbook.',
                es: 'Adoptar una estructura de gobierno y de personal apropiada para instituciones teológicas según los estándares y criterios del TMAI Handbook.',
            },
            evidenceKind: 'upload',
            gating: true,
            sourceRef: ['Statement of Intent (mar-2022), §4 LEADERSHIP — L1'],
        },
        {
            id: 'MEMBER_LEAD_SENDING_AGENCY_APPROVAL',
            stage: 'member',
            category: 'leadership',
            label: {
                en: 'Each leader of the Training Ministry has to show approval by their sending agency and/or local church to pursue TMAI Partnership.',
                es: 'Cada líder del ministerio de formación debe mostrar la aprobación de su agencia enviadora y/o iglesia local para buscar la asociación con TMAI.',
            },
            evidenceKind: 'upload',
            gating: true,
            sourceRef: ['Statement of Intent (mar-2022), §4 LEADERSHIP — L4'],
        },
        {
            id: 'MEMBER_FIN_ANNUAL_BUDGET',
            stage: 'member',
            category: 'finances',
            label: {
                en: 'Submit an annual revenue and expense budget.',
                es: 'Presentar un presupuesto anual de ingresos y egresos.',
            },
            evidenceKind: 'upload',
            gating: true,
            sourceRef: ['Statement of Intent (mar-2022), §4 FINANCES — F9'],
        },
        {
            id: 'MEMBER_FIN_ANNUAL_ACTUALS_REPORT',
            stage: 'member',
            category: 'finances',
            label: {
                en: 'Provide annual reporting on actual revenue and expenses vs. budget, as well as reporting on how operating grant funds were used.',
                es: 'Proveer un informe anual de ingresos y egresos reales vs. presupuesto, además del informe de cómo se usaron los fondos de subvención operativa.',
            },
            evidenceKind: 'upload',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 FINANCES — F10'],
        },
        {
            id: 'MEMBER_FIN_QUARTERLY_GRANT_REPORT',
            stage: 'member',
            category: 'finances',
            label: {
                en: 'Provide quarterly reporting on how special project grant funds were used (i.e. construction projects, technology upgrades, translation projects, etc.).',
                es: 'Proveer informes trimestrales de cómo se usaron los fondos de subvención de proyectos especiales (p. ej. construcción, mejoras tecnológicas, proyectos de traducción, etc.).',
            },
            evidenceKind: 'upload',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 FINANCES — F11'],
        },
        {
            id: 'MEMBER_FIN_ACCURATE_RECORDS',
            stage: 'member',
            category: 'finances',
            label: {
                en: 'Keep accurate financial records, receipts, and substantiation of expenses.',
                es: 'Mantener registros financieros, recibos y sustentación de gastos precisos.',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 FINANCES — F12'],
        },
        {
            id: 'MEMBER_FIN_REPUTABLE_SOFTWARE',
            stage: 'member',
            category: 'finances',
            label: {
                en: 'Utilize a recognized and reputable financial software.',
                es: 'Utilizar un software financiero reconocido y de buena reputación.',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 FINANCES — F13'],
        },
        {
            id: 'MEMBER_DOCTRINAL_ANNUAL_SIGNATURE',
            stage: 'member',
            category: 'doctrinal',
            label: {
                en: 'Signature by faculty, staff, and leadership on the TMAI doctrinal statement (if a faculty or staff member has a disagreement or has not yet formed an opinion in certain areas of the TMAI doctrinal statement, they must send a written explanation to TMAI).',
                es: 'Firma de la facultad, el personal y el liderazgo en la declaración doctrinal de TMAI (si un miembro de la facultad o del personal tiene un desacuerdo o aún no ha formado una opinión en ciertas áreas, debe enviar una explicación escrita a TMAI).',
            },
            evidenceKind: 'derived',
            derivedIndicator: 'DOCTRINAL_ATTESTATION_RATE',
            gating: true,
            sourceRef: ['Statement of Intent (mar-2022), §4 DOCTRINAL — D1'],
        },
        {
            id: 'MEMBER_ADMIN_ORG_CHART',
            stage: 'member',
            category: 'administrative',
            label: {
                en: 'Demonstrate an administrative structure for theological institutions according to the standards and criteria of TMAI in the TMAI Handbook. An org-chart must be in place that demonstrates the core functions of a TMAI Training Ministry (listing all staff, faculty, and board members).',
                es: 'Demostrar una estructura administrativa para instituciones teológicas según los estándares y criterios de TMAI en el TMAI Handbook. Debe existir un organigrama que demuestre las funciones centrales de un ministerio de formación TMAI (con todo el personal, facultad y miembros de la junta).',
            },
            evidenceKind: 'upload',
            gating: true,
            sourceRef: ['Statement of Intent (mar-2022), §4 ADMINISTRATIVE — A1'],
        },
        {
            id: 'MEMBER_ADMIN_STAFF_DISCIPLESHIP_PLAN',
            stage: 'member',
            category: 'administrative',
            label: {
                en: 'A plan must be in place to disciple and train staff and faculty.',
                es: 'Debe existir un plan para discipular y capacitar al personal y a la facultad.',
            },
            evidenceKind: 'upload',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 ADMINISTRATIVE — A2'],
        },
        {
            id: 'MEMBER_ACADPROG_BSC_CAP',
            stage: 'member',
            category: 'academicProgram',
            label: {
                en: 'Offer at least the Biblical Studies Certificate CAP of TMAI.',
                es: 'Ofrecer al menos el CAP del Certificado en Estudios Bíblicos de TMAI.',
            },
            evidenceKind: 'derived',
            derivedIndicator: 'CAP_BSC_STATUS',
            gating: true,
            sourceRef: ['Statement of Intent (mar-2022), §4 ACADEMIC PROGRAM — P2'],
        },
        {
            id: 'MEMBER_ACADPROG_ANNUAL_CAP_REPORT',
            stage: 'member',
            category: 'academicProgram',
            label: {
                en: 'Completion and submission of the "Annual CAP Report."',
                es: 'Completar y presentar el "Annual CAP Report".',
            },
            evidenceKind: 'upload',
            gating: true,
            sourceRef: ['Statement of Intent (mar-2022), §4 ACADEMIC PROGRAM — P4'],
        },
        {
            id: 'MEMBER_ACADPROG_COURSE_OVERVIEW',
            stage: 'member',
            category: 'academicProgram',
            label: {
                en: 'Completion and submission of the course overview for the next two years. (Ministry Plan)',
                es: 'Completar y presentar el resumen de cursos para los próximos dos años. (Plan ministerial)',
            },
            evidenceKind: 'upload',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 ACADEMIC PROGRAM — P5'],
        },
        {
            id: 'MEMBER_VISION_MATURITY_PRIORITIES',
            stage: 'member',
            category: 'visionMissionMaturity',
            label: {
                en: 'The vision and mission of TMAI for its members and partners is to mature and multiply, so that TMAI members and partners are able through their faithful ministry to grow in all areas of their ministries for a long-lasting ministry that brings glory to God. There are three areas in the maturity process that TMAI gives priority to: Indigenization (the discipleship and training of Nationals is one of the core Ministry Distinctives of TMAI); Self-Sustainability (it needs to be the goal and mission of a TMAI partner to become more and more self-sustainable); Academic Growth (the default approach of TMAI for new partners is to offer basic theological training at the undergraduate level as the beginning phase of each theological training, but with the clear strategic plan to grow and mature over time to more advanced training — not for all). All three areas are part of the national- and regional-strategic plan of TMAI.',
                es: 'La visión y misión de TMAI para sus miembros y socios es madurar y multiplicarse, de modo que crezcan en todas las áreas de su ministerio para un ministerio duradero que glorifique a Dios. TMAI prioriza tres áreas del proceso de madurez: Indigenización (el discipulado y la formación de nacionales es uno de los distintivos ministeriales centrales de TMAI); Autosustentabilidad (debe ser la meta y misión de un socio de TMAI volverse cada vez más autosustentable); Crecimiento Académico (el enfoque por defecto de TMAI para nuevos socios es ofrecer formación teológica básica de nivel de pregrado como fase inicial, pero con un plan estratégico claro de crecer y madurar con el tiempo hacia formación más avanzada — no para todos). Las tres áreas son parte del plan estratégico nacional y regional de TMAI.',
            },
            evidenceKind: 'declaration',
            gating: false,
            sourceRef: ['Statement of Intent (mar-2022), §4 VISION & MISSION FOR MATURITY — V1'],
        },
    ],
    applicationPacket: [
        {
            id: 'PKT_THEO_DOCTRINAL_SIGNATURES',
            category: 'doctrinal',
            label: {
                en: 'Signatures of all faculty and staff on the TMAI doctrinal statement',
                es: 'Firmas de toda la facultad y el personal en la declaración doctrinal de TMAI.',
            },
            evidenceKind: 'attestation',
            criterionId: 'CAND_THEO_ANNUAL_DOCTRINAL_SIGNATURE',
        },
        {
            id: 'PKT_ACAD_PROGRAM_MISSIONS',
            category: 'academic',
            label: {
                en: 'Mission statement for each degree program offered',
                es: 'Declaración de propósito de cada programa ofrecido.',
            },
            evidenceKind: 'derived',
            criterionId: 'CAND_ACAD_PROGRAM_MISSION_STATEMENTS',
        },
        {
            id: 'PKT_ACAD_COURSE_DESCRIPTIONS',
            category: 'academic',
            label: {
                en: 'Course description for every course taught',
                es: 'Descripción de curso para cada curso impartido.',
            },
            evidenceKind: 'derived',
            criterionId: 'CAND_ACAD_COURSE_DESCRIPTIONS',
        },
        {
            id: 'PKT_ACAD_OFFERINGS_CALENDAR',
            category: 'academic',
            label: {
                en: 'Course offerings calendars for the next two years',
                es: 'Calendarios de oferta de cursos para los próximos dos años.',
            },
            evidenceKind: 'upload',
            criterionId: 'CAND_ACAD_COURSE_OFFERINGS_CALENDAR',
        },
        {
            id: 'PKT_ACAD_PROGRAM_DESCRIPTIONS',
            category: 'academic',
            label: {
                en: 'Description of each degree program',
                es: 'Descripción de cada programa.',
            },
            evidenceKind: 'derived',
            criterionId: 'CAND_ACAD_PROGRAM_DESCRIPTIONS',
        },
        {
            id: 'PKT_ADMIN_BUDGET',
            category: 'administrative',
            label: {
                en: 'Full Training Center budget, broken out by program',
                es: 'Presupuesto completo del centro, desglosado por programa.',
            },
            evidenceKind: 'upload',
            criterionId: 'CAND_ADMIN_BUDGET_BY_PROGRAM',
        },
        {
            id: 'PKT_ADMIN_APPLICATION_PROCESS',
            category: 'administrative',
            label: {
                en: 'Description of the student application process',
                es: 'Descripción del proceso de postulación de estudiantes.',
            },
            evidenceKind: 'upload',
        },
        {
            id: 'PKT_ADMIN_ENROLLMENT_STRATEGY',
            category: 'administrative',
            label: {
                en: 'Description of enrollment/recruitment strategy',
                es: 'Descripción de la estrategia de matrícula y captación.',
            },
            evidenceKind: 'upload',
        },
        {
            id: 'PKT_ADMIN_FACILITY',
            category: 'administrative',
            label: {
                en: 'Description of the training facility',
                es: 'Descripción de las instalaciones de formación.',
            },
            evidenceKind: 'upload',
        },
        {
            id: 'PKT_ADMIN_PLACEMENT_STRATEGY',
            category: 'administrative',
            label: {
                en: 'Description of strategy for placement of graduates',
                es: 'Descripción de la estrategia de colocación de egresados.',
            },
            evidenceKind: 'upload',
        },
        {
            id: 'PKT_ADMIN_CHURCH_ENDORSEMENT',
            category: 'administrative',
            label: {
                en: 'Endorsement letter from the network of churches sending students',
                es: 'Carta de respaldo de la red de iglesias que envía estudiantes.',
            },
            evidenceKind: 'upload',
        },
        {
            id: 'PKT_ADMIN_PARTNER_TC_ENDORSEMENT',
            category: 'administrative',
            label: {
                en: 'Endorsement letter by partner TMAI training center leader',
                es: 'Carta de respaldo del líder del centro de formación TMAI asociado.',
            },
            evidenceKind: 'upload',
        },
        {
            id: 'PKT_PERS_FACULTY_LISTING',
            category: 'personnel',
            label: {
                en: 'Listing of all faculty and advisory council members',
                es: 'Nómina de toda la facultad y del consejo asesor.',
            },
            evidenceKind: 'derived',
            criterionId: 'CAND_PERS_FACULTY_ROSTER',
        },
        {
            id: 'PKT_PERS_STUDENT_GRADUATE_LISTING',
            category: 'personnel',
            label: {
                en: 'Listing of students and graduates including their locations and ministries',
                es: 'Nómina de estudiantes y egresados incluyendo ubicación y ministerio.',
            },
            evidenceKind: 'derived',
            criterionId: 'CAND_PERS_STUDENT_GRADUATE_ROSTER',
            containsPii: true,
        },
        {
            id: 'PKT_PERS_EXPAT_ELDER_ENDORSEMENT',
            category: 'personnel',
            label: {
                en: 'Endorsement letter by sending agency/local church(es) affirming the elder qualifications of the expats',
                es: 'Carta de respaldo de la agencia enviadora o iglesias locales afirmando las cualificaciones de anciano del personal expatriado.',
            },
            evidenceKind: 'upload',
            criterionId: 'CAND_PERS_ELDER_QUALIFICATION_AFFIRMATION',
        },
        {
            id: 'PKT_PERS_ORG_CHART',
            category: 'personnel',
            label: {
                en: 'Organizational chart listing all staff, faculty, and board members',
                es: 'Organigrama con todo el personal, facultad y miembros de la junta.',
            },
            evidenceKind: 'upload',
            criterionId: 'CAND_ADMIN_ORG_CHART',
        },
        {
            id: 'PKT_PLAN_1_AND_5_YEAR',
            category: 'administrative',
            label: {
                en: 'One-year and five-year plan per the TMAI template.',
                es: 'Plan a uno y cinco años según la plantilla de TMAI.',
            },
            evidenceKind: 'upload',
            criterionId: 'CAND_ADMIN_ONE_AND_FIVE_YEAR_PLAN',
        },
    ],
};
/** Returns the criteria a TC must complete AT `stage` to advance to the next one
 *  (anchoring rule — see MembershipCriterion.stage). Empty array for a stage with
 *  no criteria; never throws. Mirrors `getCapDefinition`'s lookup shape. */
export function getMembershipStageCriteria(framework, stage) {
    return framework.criteria.filter((c) => c.stage === stage);
}
//# sourceMappingURL=tmai-membership-2022-03.js.map